import { Disclosure } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/outline";
import { isEmpty, map, values } from "lodash";
import React, { useEffect, useMemo, useRef } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../components/CurrencySelector";
import ExternalLink from "../../components/ExternalLink";
import { useAppearance } from "../../context/appearance";
import { useAuthentication } from "../../context/authentication";
import { generateReturnToUrl } from "../../context/authentication/AuthenticationProvider";
import { useBillingPlans } from "../../context/billingPlans";
import { useMembers } from "../../context/members";
import { useStats } from "../../context/stats";
import { useViewport } from "../../context/viewport";
import { useWorkspaces } from "../../context/workspaces";
import classNames from "../../utils/classNames";
import { breakpoints } from "../../utils/constants";
import { PaymentStep, PlanType } from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import { useHntMarket } from "../../utils/useHntMarket";
import PlanCard from "./PlanCard";
import PlansUpgrade from "./PlansUpgrade";

interface Props extends RouteComponentProps {}

const PricingPage: React.FC<Props> = (props) => {
  const { plans, newPlan, setNewPlan, setPaymentStep, getPrePaidPlanByType } =
    useBillingPlans();
  const { width } = useViewport();
  const { currentWorkspace } = useWorkspaces();
  const { authenticationState } = useAuthentication();
  const isMobile = useMemo(() => width < breakpoints.lg, [width]);
  const containerUpgradeRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { isDark } = useAppearance();
  const { isCurrentUserAdmin } = useMembers();
  const { hntMarket } = useHntMarket();
  const { stats } = useStats();

  const communityPlan = useMemo(
    () => plans?.find((item) => item.type === PlanType.community),
    [plans]
  );

  const renderPlanSelection = useMemo(() => {
    if (isEmpty(plans)) {
      return (
        <span className="flex w-full h-10 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
      );
    }

    return map(plans, (plan) => (
      <span
        key={plan.name}
        onClick={() => setNewPlan((prevState) => ({ ...prevState, plan }))}
        className={classNames(
          "text-sm w-full lg:py-0 py-3 lg:w-auto lg:justify-center dark:text-gray-200 font-semibold lg:border-r border-b lg:last:border-b last:border-b-0 lg:border-t pl-2 lg:px-5 h-full cursor-pointer flex items-center border-gray-300 dark:border-gray-500 lg:first:border-l last:border-l-0 lg:last:rounded-r-lg lg:first:rounded-l-lg",
          newPlan.plan?.type === plan.type
            ? "bg-hotspotty-400 dark:bg-hotspotty-400 border-hotspotty-400 text-white dark:text-gray-200"
            : "bg-white dark:bg-gray-800"
        )}
      >
        {plan.name}
      </span>
    ));
  }, [newPlan.plan?.type, plans, setNewPlan]);

  const plansContainer = useMemo(
    () => document.getElementById("plans-container"),
    []
  );

  useEffect(() => {
    plansContainer?.scrollTo({ top: 0, behavior: "smooth" });
  }, [plansContainer]);

  const ableToShowCheckoutButton = useMemo(
    () =>
      newPlan.plan &&
      newPlan.plan?.type !== PlanType.community &&
      currentWorkspace &&
      currentWorkspace?.active_plan?.type === PlanType.community &&
      isCurrentUserAdmin,
    [currentWorkspace, isCurrentUserAdmin, newPlan.plan]
  );

  const ableToShowUpgradeButton = useMemo(
    () =>
      currentWorkspace &&
      currentWorkspace?.active_plan?.type !== PlanType.community,
    [currentWorkspace]
  );

  const averageHotspotRewardsPerMonthInUsd = useMemo(
    () =>
      hntMarket && stats?.dailyAverageHotspotRewards
        ? stats.dailyAverageHotspotRewards *
          DAYS_PER_MONTH *
          hntMarket.prices[CurrencyKey.usd]
        : undefined,
    [hntMarket, stats?.dailyAverageHotspotRewards]
  );

  return (
    <div
      id="plans-container"
      className="flex flex-col w-full items-center overflow-y-auto overflow-x-hidden pb-10 z-10"
    >
      <div className="flex flex-col w-full max-w-7xl mx-auto px-4">
        <div className="flex flex-col w-full mt-4">
          <div className="flex w-full flex-col mt-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-baseline">
              <div className="flex-1">
                <p className="text-xl sm:text-5xl font-bold sm:font-extrabold dark:text-white">
                  Step 1 - Select feature plan
                </p>
                <span className="block text-sm sm:text-base text-justify font-light sm:leading-8 mt-4 dark:text-gray-300">
                  Select a plan for your{" "}
                  <ExternalLink
                    text="workspace"
                    url="https://docs.hotspotty.net/features/monitoring-and-managing/workspace"
                    className="inline-flex mx-1"
                  />{" "}
                  based on the features you need, then add hotspots and super
                  simulations in step 2.
                </span>
              </div>

              <div className="flex justify-end mt-4 lg:mt-0">
                <Link
                  to="/pricing/compare-feature-plans"
                  className="flex-1 lg:flex-none flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                >
                  Compare feature plans
                </Link>
              </div>
            </div>
          </div>
          <div
            className={classNames(
              isMobile && "horizontal-snap-scroll justify-start py-2 -my-2",
              "inline-flex w-full h-full"
            )}
          >
            {!isEmpty(plans) ? (
              <div className="flex w-full">
                {values(plans).map((plan) => (
                  <PlanCard
                    showActionButton={false}
                    isMobile={isMobile}
                    plan={plan}
                    className="mx-1 xl:mx-2"
                    key={plan.uuid}
                    onClickToShowUpgrade={() => {
                      containerUpgradeRef.current?.scrollIntoView({
                        behavior: "smooth"
                      });
                    }}
                  />
                ))}
                <div
                  className={classNames(
                    "mx-1 xl:mx-2 flex flex-col justify-between relative w-3/12 h-full rounded-lg border first:ml-0 last:mr-0 bg-white dark:bg-gray-800 dark:border-gray-500",
                    isMobile ? "horizontal-snap-scroll-child mx-0.5" : "mx-2"
                  )}
                >
                  <div className="flex flex-col items-center justify-center py-6">
                    <span className="font-semibold text-3xl capitalize">
                      Enterprise
                    </span>
                    <div className="flex items-center h-20">
                      <button className="flex-none items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none">
                        <a
                          className="text-white"
                          href="https://hotspotty.net/request-a-demo/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Contact us
                        </a>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 justify-between w-full py-4 px-5 border-t dark:border-gray-500">
                    <div>
                      <div className="text-xs font-bold text-gray-800 dark:text-gray-300 py-2">
                        Everything in Business, plus
                      </div>
                      {[
                        "Unlimited workspaces",
                        "Unlimited hotspots & simulations",
                        "White label host dashboard (soon)",
                        "Priority support"
                      ].map((item) => (
                        <div
                          key={item}
                          className="flex break-all items-start text-left text-xs font-medium text-gray-800 dark:text-gray-300 py-2"
                        >
                          <CheckIcon className="w-4 flex-shrink-0 text-green-400 mr-2" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              dummyArray.map((item) => (
                <div
                  key={item}
                  className={classNames(
                    isMobile && "horizontal-snap-scroll-child mx-0.5",
                    "flex flex-col justify-between relative w-4/12 h-full mx-2 rounded-lg shadow-md border first:ml-0 last:mr-0 bg-white dark:bg-gray-900 dark:border-gray-500"
                  )}
                >
                  <div className="flex flex-col items-center py-10 rounded-tl-lg rounded-tr-lg dark:bg-gray-800">
                    <span className="flex w-4/12 h-4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                    <span className="flex w-7/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                  </div>
                  <div className="flex flex-col flex-1 justify-between w-full py-5 px-5 rounded-bl-lg rounded-br-lg border-t dark:border-gray-500 dark:bg-gray-900">
                    <span className="flex w-6/12 h-4 mt-2 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                    <span className="flex w-8/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                    <span className="flex w-/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />

                    <div className="flex flex-col items-center mt-40">
                      <span className="flex w-10/12 h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                      <span className="flex w-full h-4 mt-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div ref={containerUpgradeRef}>
            <p className="text-xl sm:text-5xl font-bold sm:font-extrabold mt-10 sm:mt-14 dark:text-white">
              Step 2 - Add hotspots &amp; super simulations
            </p>
            <span className="block text-sm sm:text-base text-justify font-light sm:leading-8 mt-4 dark:text-gray-300">
              Enter the expected number of hotspots and monthly simulation
              credits you need and benefit from discounted prices.
            </span>

            <div className="w-full flex flex-col mt-6 sm:mt-10">
              <div className="flex w-full flex-col lg:flex-row lg:justify-between lg:items-end">
                <div className="flex-1 flex flex-col">
                  <span className="dark:text-gray-200 mb-2">
                    Select a feature plan:
                  </span>
                  <div className="flex flex-col lg:flex-row items-center rounded-md lg:h-10 mb-6 lg:mb-0 dark:text-gray-800 lg:border-0 border overflow-hidden dark:border-gray-500">
                    {renderPlanSelection}
                  </div>
                </div>

                <div
                  className={classNames(
                    "flex-none flex items-center rounded-lg h-full dark:text-gray-800",
                    newPlan.plan && newPlan.plan?.type !== PlanType.community
                      ? "flex"
                      : "hidden lg:invisible lg:flex"
                  )}
                >
                  <div className="overflow-hidden flex flex-col py-2 px-8 items-center h-full justify-center border border-r-0 bg-white dark:border-gray-500 dark:bg-gray-800 rounded-l-lg">
                    <span className="sm:flex sm:items-center text-xs sm:text-sm dark:text-white mb-1">
                      Hotspotty {newPlan.plan?.name}
                    </span>
                    <span className="text-md sm:text-2xl font-bold dark:text-white truncate">
                      {((newPlan.plan?.price || 0) / 100).toLocaleString(
                        "en-US",
                        USD_CURRENCY_LOCALE_STRING_CONFIG
                      )}
                    </span>
                  </div>
                  <div className="overflow-hidden flex flex-col py-2 px-8 items-center h-full justify-center border bg-white border-r-0 dark:border-gray-500 dark:bg-gray-800">
                    <span className="sm:flex sm:items-center text-xs sm:text-sm dark:text-white mb-1">
                      {newPlan.hotspotAmount.toLocaleString(undefined)} hotspot
                      {newPlan.hotspotAmount === 1 ? "" : "s"}
                    </span>
                    <span className="text-md sm:text-2xl font-bold dark:text-white">
                      {newPlan.hotspotAmountPrice.toLocaleString(
                        "en-US",
                        USD_CURRENCY_LOCALE_STRING_CONFIG
                      )}
                    </span>
                  </div>
                  <div className="overflow-hidden flex flex-col py-2 px-8 items-center h-full justify-center border bg-white border-r-0 dark:border-gray-500 dark:bg-gray-800">
                    <span className="sm:flex sm:items-center text-xs sm:text-sm dark:text-white mb-1">
                      {newPlan.simulationAmount.toLocaleString(undefined)}{" "}
                      simulation
                      {newPlan.simulationAmount === 1 ? "" : "s"}
                    </span>
                    <span className="text-md sm:text-2xl font-bold dark:text-white">
                      {newPlan.simulationAmountPrice.toLocaleString(
                        "en-US",
                        USD_CURRENCY_LOCALE_STRING_CONFIG
                      )}
                    </span>
                  </div>
                  <div className="overflow-hidden flex flex-col py-2 px-8 items-center h-full justify-center border bg-white dark:border-gray-500 dark:bg-gray-800 rounded-r-lg">
                    <span className="sm:flex sm:items-center text-xs sm:text-sm dark:text-white mb-1">
                      Monthly total
                    </span>
                    <span className="text-md sm:text-2xl font-bold text-hotspotty-400 dark:text-hotspotty-300">
                      {(
                        (newPlan.plan?.price || 0) / 100 +
                        newPlan.hotspotAmountPrice +
                        newPlan.simulationAmountPrice
                      ).toLocaleString(
                        "en-US",
                        USD_CURRENCY_LOCALE_STRING_CONFIG
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {newPlan.plan && newPlan.plan?.type !== PlanType.community ? (
                <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <div>
                    <PlansUpgrade
                      className="p-3 sm:p-5 h-full"
                      title="Hotspots"
                      min={1}
                      max={20000}
                      subtitle={
                        <>
                          <div>
                            <b>
                              {newPlan.hotspotAmount.toLocaleString(undefined)}
                            </b>{" "}
                            hotspots
                          </div>
                          <div>
                            <b>
                              {(newPlan.hotspotAmount * 2).toLocaleString(
                                undefined
                              )}
                            </b>{" "}
                            locations
                          </div>
                        </>
                      }
                      description={
                        newPlan.hotspotAmount > 0 &&
                        averageHotspotRewardsPerMonthInUsd
                          ? [
                              <>
                                {`${newPlan.hotspotAmount} hotspot${
                                  newPlan.hotspotAmount === 1 ? "" : "s"
                                } earn${
                                  newPlan.hotspotAmount === 1 ? "s" : ""
                                } on average ${(
                                  newPlan.hotspotAmount *
                                  averageHotspotRewardsPerMonthInUsd
                                ).toLocaleString("en-US", {
                                  style: "currency",
                                  currency: CurrencyKey.usd,
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                                })}/mo. Manage ${
                                  newPlan.hotspotAmount === 1 ? "it" : "them"
                                } for `}
                                <b>{`only ${(
                                  (newPlan.hotspotAmountPrice /
                                    (newPlan.hotspotAmount *
                                      averageHotspotRewardsPerMonthInUsd)) *
                                  100
                                ).toFixed(2)}%`}</b>{" "}
                                of these rewards!
                              </>
                            ]
                          : isMobile
                          ? undefined
                          : [<div className="h-5" />]
                      }
                      value={newPlan.hotspotAmountPrice}
                      amount={newPlan.hotspotAmount}
                      priceTiers={
                        getPrePaidPlanByType(PlanType.prePaidHotspot)
                          ?.price_tiers
                      }
                      onChange={(value) =>
                        setNewPlan((prevState) => ({
                          ...prevState,
                          hotspotAmount: value
                        }))
                      }
                    />
                  </div>

                  <div>
                    <PlansUpgrade
                      className="p-3 sm:p-5 h-full"
                      title="Simulations per month"
                      min={1}
                      max={20000}
                      subtitle={
                        <>
                          <div>
                            <b>
                              {newPlan.simulationAmount.toLocaleString(
                                undefined
                              )}
                            </b>{" "}
                            simulations
                          </div>
                          <div>
                            <b>
                              {(newPlan.simulationAmount * 2).toLocaleString(
                                undefined
                              )}
                            </b>{" "}
                            locations
                          </div>
                        </>
                      }
                      description={
                        isMobile ? undefined : [<div className="h-5" />]
                      }
                      value={newPlan.simulationAmountPrice}
                      amount={newPlan.simulationAmount}
                      priceTiers={
                        getPrePaidPlanByType(PlanType.prePaidSimulation)
                          ?.price_tiers
                      }
                      onChange={(value) =>
                        setNewPlan((prevState) => ({
                          ...prevState,
                          simulationAmount: value
                        }))
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="h-72 flex lg:mt-6 items-end border rounded-lg relative shadow-md dark:bg-gray-900 dark:border-gray-500 mt-0 overflow-hidden">
                  <img
                    className="absolute w-full h-full object-cover z-0"
                    src={
                      isDark
                        ? "/dark-container-community.svg"
                        : "/light-container-community.svg"
                    }
                    alt="community"
                  />
                  <div className="flex flex-col w-full lg:w-6/12 my-auto z-1 lg:ml-10 relative lg:px-0 px-3">
                    <span className="dark:text-white text-4xl sm:text-5xl font-bold">
                      Ready to dive in?
                    </span>
                    <span className="text-xs block sm:text-sm dark:text-gray-300 mt-4 mb-8 sm:mt-8 sm:mb-12">
                      Hotspotty Community includes {communityPlan?.hotspots}{" "}
                      hotspot, {communityPlan?.simulations} simulation and{" "}
                      {(communityPlan?.hotspots || 1) * 2} locations.
                      <br />
                      More hotspots and simulations can only be added to the Pro
                      and Business plans.
                    </span>
                    {authenticationState === "authenticated" &&
                      currentWorkspace && (
                        <div>
                          <Link
                            to={
                              authenticationState === "authenticated"
                                ? "/workspace/dashboard"
                                : `/login?${generateReturnToUrl(
                                    history.location
                                  )}`
                            }
                            className="mt-4 px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500"
                          >
                            {authenticationState === "authenticated"
                              ? "Open Workspace"
                              : "Sign in"}
                          </Link>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {ableToShowCheckoutButton && (
                <div className="flex w-full justify-center sm:justify-end mt-6">
                  <button
                    onClick={() => {
                      setPaymentStep(PaymentStep.choosePlan);
                      history.push("/checkout");
                    }}
                    className="w-full sm:w-auto sm:ml-auto text-xs shadow-md py-2 px-10 text-white rounded-md border-0 bg-hotspotty-400 focus:outline-none hover:bg-hotspotty-400"
                  >
                    Proceed to checkout
                  </button>
                </div>
              )}

              {ableToShowUpgradeButton && (
                <button
                  onClick={() => {
                    history.push(`/workspace/settings/billing`);
                  }}
                  className="mt-6 w-full sm:w-auto sm:ml-auto text-xs shadow-md py-2 px-10 text-white rounded-md border-0 bg-hotspotty-400 focus:outline-none hover:bg-hotspotty-400"
                >
                  Update Billing
                </button>
              )}

              {authenticationState === "unauthenticated" && (
                <Link
                  to={`/login?${generateReturnToUrl(history.location)}`}
                  className="w-full sm:w-auto sm:ml-auto mt-6 px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500"
                >
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </div>

        <p className="text-xl sm:text-5xl font-bold sm:font-extrabold mt-10 sm:mt-14 dark:text-white">
          Frequently asked questions
        </p>
        <dl className="mm-0 sm:mt-6 space-y-4 divide-y divide-gray-200 dark:divide-gray-500">
          {faqs.map((faq) => (
            <Disclosure as="div" key={faq.question} className="pt-4 sm:pt-6">
              {({ open }) => (
                <>
                  <dt className="text-lg">
                    <Disclosure.Button className="text-left w-full flex justify-between items-start text-gray-400 dark:text-white">
                      <span className="font-medium text-gray-900 dark:text-gray-200">
                        {faq.question}
                      </span>
                      <span className="ml-2 sm:ml-6 h-7 flex items-center">
                        <ChevronDownIcon
                          className={classNames(
                            open ? "-rotate-180" : "rotate-0",
                            "h-6 w-6 transform"
                          )}
                          aria-hidden="true"
                        />
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className="mt-2">
                    <p className="font-light sm:leading-8 text-justify text-gray-500 dark:text-gray-200">
                      {faq.answer}
                    </p>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </dl>
      </div>
    </div>
  );
};

export default PricingPage;

//
// Utils
//

const dummyArray = ["1", "2", "3", "4"];

export const whatAreSuperSimulationsText =
  "Super simulations allow you to assess the potential of a location for a hotspot installation. It uses elevation profiles to calculate which hotspots and other workspace locations have a clear line of sight from the given location at a specified height above the ground. These results are then further processed using Helium's Proof of Coverage reward mechanisms resulting in a PoC Score and a comparison to other hotspots across the network. This classification allows you to compare different locations and installation heights so you can make an informed decision about your hotspot deployments.";

const faqs = [
  {
    question: "What are workspaces and can I have more than one workspace?",
    answer: (
      <>
        For more information about workspaces{" "}
        <ExternalLink
          className="inline-flex text-hotspotty-300"
          text="click here"
          url="https://docs.hotspotty.net/features/monitoring-and-managing/workspace"
        />
      </>
    )
  },
  {
    question: "What are locations and how can they be used?",
    answer: (
      <>
        For more information about locations{" "}
        <ExternalLink
          className="inline-flex text-hotspotty-300"
          text="click here"
          url="https://docs.hotspotty.net/features/planning-and-optimizing/manage-locations#what-can-you-do-with-locations"
        />
      </>
    )
  },
  {
    question: "What are super simulations?",
    answer: whatAreSuperSimulationsText
  },
  {
    question: "What happens with my workspace if I don't pick a paid plan?",
    answer:
      "Your workspace will be limited to the free community plan which provides 1 hotspot, 1 simulation and 2 locations. More hotspots and simulations can only be added to the Pro and Business plans."
  },
  {
    question: "Can I share my Discord login with another user?",
    answer: (
      <>
        Hotspotty Business Plan provides collaboration for upto 10 different
        team members. It is not recommended to share a single Discord login
        while working on the same data in your Hotspotty workspace. There are
        mechanisms in place to disincentivize sharing accounts in an unintented
        way. For more information about Team collaboration{" "}
        <ExternalLink
          className="inline-flex text-hotspotty-300"
          text="click here"
          url="https://docs.hotspotty.net/features/monitoring-and-managing/settings#members"
        />
      </>
    )
  }
];

const DAYS_PER_MONTH = 31;
